import { storageUtils } from './localStorage';

export const GROUPS = ['red', 'orange', 'green', 'gold'];

export const GROUP_LABELS = {
  red: 'Red',
  orange: 'Orange',
  green: 'Green',
  gold: 'Gold',
};

export const GROUP_CHILI_IDS = {
  red: ['1', '5', '9', '13'],
  orange: ['2', '6', '10', '14'],
  green: ['3', '7', '11', '15'],
  gold: ['4', '8', '12', '16'],
};

export const isGroup = (value) => GROUPS.includes(value);

// Firestore is downloaded only when a page needs it, which keeps the first
// page load light on phones. The Lite SDK covers everything used here and
// fails fast when offline instead of waiting to reconnect.
const loadFirestore = () =>
  Promise.all([import('../firebase'), import('firebase/firestore/lite')]);

export const preloadFirestore = () => {
  loadFirestore().catch(() => {});
};

// Sign-ups made while running `yarn start` are stored separately so testing
// never skews the live event's group counts.
const eventId = () => {
  const year = storageUtils.getCurrentYear();
  return process.env.NODE_ENV === 'production' ? `${year}` : `${year}-dev`;
};

const normalizeEmail = (email) => email.trim().toLowerCase();

// Firestore document IDs can't contain "/", which emails very rarely do.
const emailId = (email) => email.replace(/\//g, '%2F');

const hashEmail = (email) => {
  let hash = 0;
  for (let i = 0; i < email.length; i += 1) {
    hash = (hash * 31 + email.charCodeAt(i)) >>> 0;
  }
  return hash;
};

// A stable tie-break spreads a burst of sign-ups across tied groups instead of
// sending every person who read the same counts to the first group.
const leastAssigned = (counts, email) => {
  const lowestCount = Math.min(...GROUPS.map((group) => counts[group]));
  const tiedGroups = GROUPS.filter(
    (group) => counts[group] === lowestCount
  );
  return tiedGroups[hashEmail(email) % tiedGroups.length];
};

// Used when Firestore can't be reached. The same email always maps to the same
// group, so retrying doesn't bounce a taster between groups.
const groupFromEmail = (email) => GROUPS[hashEmail(email) % GROUPS.length];

// Returns the taster's group: their existing one if this email has signed up
// before, otherwise the group with the fewest tasters. Never rejects.
export async function assignGroup({ email, name }) {
  const normalizedEmail = normalizeEmail(email);
  let selectedGroup;

  try {
    const [
      { db },
      { doc, getDoc, increment, runTransaction, serverTimestamp },
    ] = await loadFirestore();
    const eventRef = doc(db, 'chili', eventId());
    const tasterRef = doc(eventRef, 'tasters', emailId(normalizedEmail));

    const saved = await getDoc(tasterRef);
    if (isGroup(saved.get('group'))) return saved.get('group');

    const groupRefs = GROUPS.reduce(
      (refs, group) => ({
        ...refs,
        [group]: doc(eventRef, 'groups', group),
      }),
      {}
    );
    const groupDocs = await Promise.all(
      GROUPS.map((group) => getDoc(groupRefs[group]))
    );
    const counts = GROUPS.reduce(
      (result, group, index) => ({
        ...result,
        [group]: Number.isInteger(groupDocs[index].get('count'))
          ? groupDocs[index].get('count')
          : 0,
      }),
      {}
    );
    selectedGroup = leastAssigned(counts, normalizedEmail);

    // The transaction reads only this email's taster document. Different
    // sign-ups therefore do not invalidate each other. Group counts use an
    // atomic increment on one of four independent documents.
    return runTransaction(db, async (transaction) => {
      const taster = await transaction.get(tasterRef);
      if (isGroup(taster.get('group'))) return taster.get('group');

      transaction.set(tasterRef, {
        email: normalizedEmail,
        name,
        group: selectedGroup,
        assignedAt: serverTimestamp(),
      });
      transaction.set(
        groupRefs[selectedGroup],
        {
          count: increment(1),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      return selectedGroup;
    });
  } catch (error) {
    console.warn('Group assignment fell back to email-based group.', error);
    return selectedGroup || groupFromEmail(normalizedEmail);
  }
}

// Logs that a taster finished a chili's survey. Best effort: failures are
// logged and never interrupt the page.
export async function recordCompletion({ email, name, group, chiliId }) {
  if (!email || !isGroup(group) || !/^\d{1,3}$/.test(chiliId)) return;

  const normalizedEmail = normalizeEmail(email);
  try {
    const [{ db }, { doc, serverTimestamp, setDoc }] = await loadFirestore();
    const completionId = `${emailId(normalizedEmail)}_${chiliId}`;
    await setDoc(doc(db, 'chili', eventId(), 'completions', completionId), {
      email: normalizedEmail,
      name: (name || '').slice(0, 100),
      group,
      chiliId,
      completedAt: serverTimestamp(),
    });
  } catch (error) {
    // Expected when a completion link is reopened: each one is stored once.
    console.warn('Could not record survey completion.', error);
  }
}
